import React, { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Loader2, Send, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLASSES, INSTITUTE } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || "YOUR_SERVICE_ID";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID";
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || "YOUR_PUBLIC_KEY";

/**
 * Lead-capture dialog used by the Resource Center.
 * Captures Name + Mobile + Standard, then opens WhatsApp with the
 * resource filename so the team can send the PDF back manually.
 */
export default function LeadCaptureDialog({ resource, open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", standard: "" });
  const [loading, setLoading] = useState(false);

  const reset = () => setForm({ name: "", phone: "", standard: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.standard) {
      toast.error("Please fill name, mobile and standard.");
      return;
    }
    if (!/^[+0-9\s-]{7,20}$/.test(form.phone)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }

    setLoading(true);

    const payload = {
      name: form.name,
      phone: form.phone,
      email: "(resource download)",
      student_class: form.standard,
      board: "Resource Request",
      message: `Resource requested: ${resource?.name || "—"} (${resource?.filename || ""})`,
      to_email: "kothariclassesjalgaon@gmail.com",
    };

    // Email + DB backup in parallel
    await Promise.allSettled([
      emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, payload, { publicKey: EMAILJS_PUBLIC_KEY }),
      axios.post(`${API}/inquiries`, {
        name: form.name,
        phone: form.phone,
        email: "",
        student_class: form.standard,
        board: "Resource Request",
        message: payload.message,
        source: "resources",
      }),
    ]);

    setLoading(false);
    toast.success("Thank you! We will send your PDF on WhatsApp shortly.");
    reset();
    onOpenChange(false);

    // Open WhatsApp pre-filled so user can confirm
    const wText = `Hi, I requested "${resource?.name}" from the Kothari Classes Resource Center.\nName: ${form.name}\nClass: ${form.standard}`;
    window.open(
      `https://wa.me/${INSTITUTE.whatsapp_number}?text=${encodeURIComponent(wText)}`,
      "_blank"
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-none border border-gray-200 p-0" data-testid="lead-capture-dialog">
        <div className="bg-red-600 text-white px-6 py-4">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] opacity-90">
            <Download className="w-3.5 h-3.5" /> PDF Request
          </div>
          <DialogTitle className="font-display text-2xl font-black mt-1 leading-tight">
            Where should we send your PDF?
          </DialogTitle>
          {resource && (
            <DialogDescription className="text-white/90 text-xs mt-1.5 truncate">
              {resource.name}
            </DialogDescription>
          )}
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4" data-testid="lead-capture-form">
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Name *</label>
            <input
              type="text"
              name="name"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Full name"
              data-testid="lead-name-input"
              className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Mobile Number *</label>
            <input
              type="tel"
              name="phone"
              required
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+91 ..."
              data-testid="lead-phone-input"
              className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Standard *</label>
            <Select value={form.standard} onValueChange={(v) => setForm((f) => ({ ...f, standard: v }))}>
              <SelectTrigger data-testid="lead-standard-select" className="rounded-none border-gray-300 focus:ring-red-600 focus:border-red-600 py-3 h-auto text-sm">
                <SelectValue placeholder="Select standard" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {CLASSES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="lead-submit-button"
            className="btn-primary rounded-none text-sm w-full justify-center disabled:opacity-60 mt-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send My PDF</>}
          </button>

          <p className="text-[11px] text-gray-500 text-center">
            By requesting, you agree to be contacted by Kothari Classes on WhatsApp.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
